/**
 * The arranged canvas (Feature 71 D36): a column per depth from the root,
 * nodes stacked within it. Pure, over names and edges, so it knows nothing
 * of the draft. A column holding only gate nodes is narrow, since a gate is
 * a small diamond and its condition nodes take the next column (Feature 79
 * D26). A new node lands where the canvas is looking, clear of the others
 * (D33).
 */
import type { LayoutPoint } from '@optum/api-client/types';

// The spacing of an arranged canvas: one column per depth from `first`. A
// column leaves room between two nodes for a gate's marker (F72 D13).
export const COLUMN = 320;
/** The width of a column of gate nodes alone. */
export const GATE_COLUMN = 160;
const ROW = 130;
/** The spacing of a gate's condition nodes placed beside it. */
const CONDITION_ROW = 90;
/** The room a node takes from its corner. */
export interface Room {
  left: number;
  top: number;
  right: number;
  bottom: number;
}
/** A step's room; every node already placed is taken to need as much. */
export const STEP_ROOM: Room = { left: 0, top: 0, right: 184, bottom: 70 };
/** A gate's room, with its condition nodes beside it. */
export const GATE_ROOM: Room = { left: 0, top: -60, right: GATE_COLUMN + 176, bottom: 130 };
const MARGIN = 16;
// The grid a new node's landing is searched on, and how far out it looks.
const LANDING_STEP = 40;
const LANDING_REACH = 20;

/** Each node's depth: the longest path of edges from the root to it. */
function depths(
  names: readonly string[],
  edges: readonly { from: string; to: string }[],
  root: string,
): Map<string, number> {
  const depth = new Map<string, number>([[root, 0]]);
  // At most one pass per node settles the longest paths of a loop-free graph.
  for (let pass = 0; pass < names.length; pass += 1) {
    for (const edge of edges) {
      const from = depth.get(edge.from);
      if (from !== undefined && (depth.get(edge.to) ?? -1) < from + 1) depth.set(edge.to, from + 1);
    }
  }
  // A node nothing reaches yet sits one column past the root.
  for (const name of names) if (!depth.has(name)) depth.set(name, 1);
  return depth;
}

/** Positions by depth for every node: a column per depth, nodes stacked within it. */
export function layered(
  names: readonly string[],
  edges: readonly { from: string; to: string }[],
  root: string,
  narrow: ReadonlySet<string> = new Set(),
): Record<string, LayoutPoint> {
  const depth = depths(names, edges, root);
  const columns = new Map<number, string[]>();
  for (const name of names) {
    const d = depth.get(name) ?? 1;
    columns.set(d, [...(columns.get(d) ?? []), name]);
  }
  // Each column starts where the ones before it end.
  const left = new Map<number, number>();
  let x = 0;
  for (const d of [...columns.keys()].sort((a, b) => a - b)) {
    left.set(d, x);
    x += (columns.get(d) ?? []).every((name) => narrow.has(name)) ? GATE_COLUMN : COLUMN;
  }
  const parents = new Map<string, string[]>();
  for (const edge of edges) parents.set(edge.to, [...(parents.get(edge.to) ?? []), edge.from]);
  const tallest = Math.max(...[...columns.values()].map((c) => c.length));
  const placed: Record<string, LayoutPoint> = {};
  for (const d of [...columns.keys()].sort((a, b) => a - b)) {
    // A node sits as level as it can with the nodes that lead to it, so links
    // cross as little as they can; ties keep the order given.
    const level = (name: string) => {
      const ys = (parents.get(name) ?? []).flatMap((p) => (placed[p] ? [placed[p].y] : []));
      return ys.length > 0 ? ys.reduce((a, b) => a + b, 0) / ys.length : Infinity;
    };
    const column = (columns.get(d) ?? [])
      .map((name, i) => ({ name, i, key: level(name) }))
      .sort((a, b) => (a.key === b.key ? a.i - b.i : a.key - b.key))
      .map((entry) => entry.name);
    const offset = ((tallest - column.length) * ROW) / 2;
    column.forEach((name, i) => {
      placed[name] = { x: left.get(d) ?? 0, y: offset + i * ROW };
    });
  }
  return placed;
}

/** Where the `i`th of a gate's `n` condition nodes stands beside it. */
export function besideGate(gate: LayoutPoint, i: number, n: number): LayoutPoint {
  return { x: gate.x + GATE_COLUMN, y: gate.y + (i - (n - 1) / 2) * CONDITION_ROW };
}

/**
 * `at`, or the nearest point to it where a node needing `room` overlaps no
 * node already placed and, when `view` is given, fits inside it; failing
 * that, the clear point that leaves the view least (D33).
 */
export function landing(
  placed: readonly LayoutPoint[],
  at: LayoutPoint,
  room: Room = STEP_ROOM,
  view?: Room | undefined,
): LayoutPoint {
  const crowded = (point: LayoutPoint) =>
    placed.some(
      (p) =>
        p.x + STEP_ROOM.right + MARGIN > point.x + room.left &&
        point.x + room.right + MARGIN > p.x &&
        p.y + STEP_ROOM.bottom + MARGIN > point.y + room.top &&
        point.y + room.bottom + MARGIN > p.y,
    );
  // How far the node would stand outside the view, edge by edge.
  const outside = (point: LayoutPoint) =>
    view
      ? Math.max(0, view.left - point.x - room.left) +
        Math.max(0, point.x + room.right - view.right) +
        Math.max(0, view.top - point.y - room.top) +
        Math.max(0, point.y + room.bottom - view.bottom)
      : 0;
  const clear: { point: LayoutPoint; outside: number; distance: number }[] = [];
  for (let i = -LANDING_REACH; i <= LANDING_REACH; i += 1) {
    for (let j = -LANDING_REACH; j <= LANDING_REACH; j += 1) {
      const point = { x: at.x + i * LANDING_STEP, y: at.y + j * LANDING_STEP };
      if (crowded(point)) continue;
      clear.push({ point, outside: outside(point), distance: i * i + j * j });
    }
  }
  clear.sort((a, b) => a.outside - b.outside || a.distance - b.distance);
  return clear[0]?.point ?? at;
}
